/** @format */

import {
  AnalyticsService,
  CookieService,
  UtilityService,
  WINDOW,
} from '@shared/data-access/services';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';

import { AuthenticationService } from '@shared/authentication/data-access';
import { Chain } from '@shared/data-access/interfaces';
import { distinctUntilChanged } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

@Directive({
  selector: '[authUtilsAuthLink]',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class AuthLinkDirective implements OnInit {
  @HostBinding('attr.title') attrTitle: string | undefined;
  @HostBinding('innerHTML') innerHTML: string | undefined;

  @Input() public loggedInText = 'My Account';
  @Input() public loggedOutText = 'Sign In';
  @Input() public displayWelcomeMessage = false;
  @Input() public redirectBackToUri: boolean | string = false;
  @Input() public analyticsEventToFireOnClick = '';
  @Input() public analyticsEventDataToFireOnClick?: Record<string, unknown>;
  @Input() public manualDestination = '';

  public loggedInClass = 'loggedIn';
  public welcomeMessage = 'Welcome, {0}';

  private isLoggedIn = false;

  private readonly authService = inject(AuthenticationService);
  private readonly chain = inject(Chain);
  private readonly cookieService = inject(CookieService);
  private readonly elementRef = inject(ElementRef);
  private readonly utilityService = inject(UtilityService);
  private readonly window = inject(WINDOW);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly platformId = inject(PLATFORM_ID);

  public ngOnInit(): void {
    // Only perform actual auth checks in the browser
    if (isPlatformBrowser(this.platformId)) {
      // Check to see if logged in
      this.authService
        .getIsAuthenticated$()
        .pipe(distinctUntilChanged())
        .subscribe({
          next: (isLoggedIn: boolean) => {
            // Set the logged in status
            this.isLoggedIn = isLoggedIn;
            // Set the link text based on the logged in status
            this.setLinkStyles(this.isLoggedIn);

            // Set the link text based on the logged in status
            this.setLinkTextAndTitle(this.isLoggedIn);
            this.cdr.markForCheck();
          },
          error: () => {
            // If server, default to logged out
            this.isLoggedIn = false;

            // Set the link text based on the logged in status
            this.setLinkStyles(this.isLoggedIn);

            // Set the link text based on the logged in status
            this.setLinkTextAndTitle(this.isLoggedIn);
            this.cdr.markForCheck();
          },
        });
    } else {
      // If server, default to logged out
      this.isLoggedIn = false;

      // Set the link text based on the logged in status
      this.setLinkStyles(this.isLoggedIn);

      // Set the link text based on the logged in status
      this.setLinkTextAndTitle(this.isLoggedIn);
    }
  }

  /**
   * @description
   * Click handler for the auth link to navigate to login page. This dynamically
   * sets the redirectUri based on the page where the link was clicked
   */
  @HostListener('click', ['$event'])
  public onAuthLinkClick(event: MouseEvent): void {
    // Prevent default
    event.preventDefault();

    if (this.analyticsEventToFireOnClick && this.analyticsEventDataToFireOnClick) {
      this.analyticsService.triggerAnalyticsEvent(
        this.analyticsEventToFireOnClick,
        this.analyticsEventDataToFireOnClick,
      );
    } else if (this.analyticsEventToFireOnClick) {
      this.analyticsService.triggerAnalyticsEvent(this.analyticsEventToFireOnClick);
    }

    // Check to see if we should redirect back to the current URI for login
    if (this.redirectBackToUri && !this.isLoggedIn) {
      let route = this.window.location.pathname;

      /// adding check to allow for custom routes to be passed through
      /// if the param is a string and the length is greater than a minimum amount of characters, it replaces the default
      if (
        typeof this.redirectBackToUri === 'string' &&
        this.redirectBackToUri.length > 0 &&
        this.redirectBackToUri.includes('/')
      ) {
        route = this.redirectBackToUri;
      }

      // Redirect Target
      const targetObject = {
        target: `${route}`,
      };

      // Signup will take you to login, unless specified otherwise with manualDestination
      if (this.manualDestination === 'createAccount') {
        this.authService.createAccount(targetObject).subscribe();
      } else {
        // Need to override default redirect, manually call login method to provide URIs
        this.authService.login(targetObject).subscribe();
      }
    } else if (this.isLoggedIn) {
      this.window.location.assign(this.chain.myAccountUrlLoggedIn);
    } else {
      this.authService.login({
        target: this.chain.myAccountUrlLoggedIn,
      });
    }
  }

  /**
   * @description
   * Sets classes/styles of the auth link based on logged in status
   */
  public setLinkStyles(isLoggedIn: boolean): void {
    // Check logged in status
    if (isLoggedIn && !this.elementRef.nativeElement.classList.contains(this.loggedInClass)) {
      // Set the logged in classes/styles
      this.elementRef.nativeElement.classList.add(this.loggedInClass);
    } else if (!isLoggedIn) {
      // Remove the logged in classes/styles
      this.elementRef.nativeElement.classList.remove(this.loggedInClass);
    }
  }

  /**
   * @description
   * Sets the innerHTML text of the auth link based on inputs and logged in status
   */
  public setLinkTextAndTitle(isLoggedIn: boolean): void {
    // Check logged in status; This can only ever be true in the browser
    if (isLoggedIn) {
      // Check to see if we need to display a welcome message
      if (this.utilityService.isTruthy(this.displayWelcomeMessage)) {
        // if this cookie exists, it's the new secured athlete login flow so use the name from this cookie
        const userName = this.cookieService.get('DCSG-ATHLETE-NAME');

        /// if name is defined then we set the welcome text to the value, if not then it proceeds to getting it from the auth0Client
        if (userName) {
          this.loggedInText = this.welcomeMessage.replace('{0}', userName);
        }
      }

      // Set the logged in text
      this.innerHTML = this.loggedInText;

      // Set the logged in title
      this.attrTitle = this.loggedInText;
    } else {
      // Set the logged out text
      this.innerHTML = this.loggedOutText;

      // Set the logged out title
      this.attrTitle = this.loggedOutText;
    }
  }
}
